import React, { useRef, useEffect, useMemo, useCallback } from "react";

import "./style.css";
import "./cems_styles.css";
import "./alcatraz_styles.css";
import { sendCommand } from "../../../ducks/uiDuck";
import { useDispatch } from "react-redux";
import {
  useDOMElementContainerReference,
  useParsedSvgRootDOMElement,
} from "./GraphicalView2DHooks";
import { status, graphicalDevice } from "../../../../theme/muiTheme";

import {
  updateMeasurementsText,
  updateDeviceAlarmEffects,
  appendTextToFilterIds,
} from "./GraphicalView2DFunctions";
import { setLastUsedCommand } from "../../../ducks/graphicsDuck";

// GRAPHICAL VIEW COMPONENT
function GraphicalView2D({
  svg = "",
  systemId = "",
  serviceModeStatus = "",
  deviceStateToGraph,
  taggedDataToGraph,
  setHelperTextFunction = () => { },
  buttonMapping,
  lastUserCommandFromUI
}) {


  // Parse `svg` and get the <svg> DOM element
  const svgRoot = useParsedSvgRootDOMElement(svg, systemId);

  // Get a reference for embedding `svgRoot` inside a React element
  const svgContainerRef = useDOMElementContainerReference(svgRoot, systemId);

  const dispatch = useDispatch();


  const graphicalUiCommandButtonPressed = useCallback((commandConfig, hwid) => {
    let command = "";
    let activeState = false;
    if (!serviceModeStatus) {
      return;
    }
    const deviceData = [...deviceStateToGraph];
    const device = deviceData.find(d => d.hwId === hwid);
    const commandState = device.commandStates.find(c => c.commandName === commandConfig.name);

    if (commandConfig.toggle === false) {
      command = commandConfig.press;
    } else {
      activeState = commandState.activeState;
      command = activeState ? commandConfig.release : commandConfig.press;
    }

    if (serviceModeStatus === "ManualActive" || commandState.independent) {
      setHelperTextFunction({
        text: `Command given: ${hwid}-${command}.`,
        textColor: "white"
      });

      let isToggle = commandConfig.toggle;
      let isActivating = activeState === false ? true : false; // setLastUsedCommand needs this

      dispatch(setLastUsedCommand({
        device: hwid,
        hwId: hwid,
        commandName: commandConfig.name,
        isActivating: isActivating,
        timeCalled: Date.now(),
        systemId
      }));

      dispatch(sendCommand({
        argsProps: { HwId: hwid, Command: command, Value: "" },
        commandType: { isToggle, isActivating },
        systemId,
      }));
    } else {
      setHelperTextFunction({
        text: "Commands disabled! Please Active manual mode first.",
        textColor: "red"
      });
    }
  }, [serviceModeStatus, deviceStateToGraph, setHelperTextFunction, dispatch, systemId]);



  const clickHandlers = useRef(new Map());

  const handleClick = useCallback((command, device) => () => {
    graphicalUiCommandButtonPressed(command, device.hwId);
  }, [graphicalUiCommandButtonPressed]);

  // Bind click handlers to button SVG groups
  useEffect(() => {
    const addEventListeners = () => {
      buttonMapping.forEach(device => {
        device.commands.forEach(command => {
          const clickHandler = handleClick(command, device);
          const buttonElement = svgRoot?.querySelector(command.buttonTag);
          if (buttonElement) {
            buttonElement.addEventListener("click", clickHandler);
            clickHandlers.current.set(command, clickHandler);
          }
        });
      });
    };

    const removeEventListeners = () => {
      clickHandlers.current.forEach((clickHandler, command) => {
        const buttonElement = svgRoot?.querySelector(command.buttonTag);
        if (buttonElement) {
          buttonElement.removeEventListener("click", clickHandler);
        }
      });
      clickHandlers.current.clear();
    };

    addEventListeners();
    return () => {
      removeEventListeners();
    };
  }, [serviceModeStatus, deviceStateToGraph, buttonMapping, svgRoot]);


  // Update device alarms and measurements
  deviceStateToGraph.forEach((device, index) => {
    updateDeviceAlarmEffects(device.deviceTag, device.deviceBgColor, svgRoot);
  });
  taggedDataToGraph.forEach(taggedData => {
    updateMeasurementsText(taggedData.data, taggedData.tag, svgRoot);
  });

  // After `svgRoot` has been initialized, we need to add unique modifier to the end ALL filter attributes and fields
  // Without this, opening multiple graphical views from cloud at the same time causes visual bugs. Visual bugs can range from mild to severe.
  useEffect(() => {
    if (svgRoot) {
      // Append unique modifier to filter IDs
      const uniqueModifierToFilterAttribute = `_${systemId}`;
      appendTextToFilterIds(svgRoot, uniqueModifierToFilterAttribute);
    }
  }, [svgRoot]);

  /* 
    The following useEffect() handles SVG element's class-attributes. We add and remove classes according to user inputs.
    deviceStateToGraph contains information on active classes that should be added.
    You can style these classes with CSS as usual. 
    For example "svg.air #line-scuAir-to-valveJunction-active" will add styles to line-scuAir-to-valveJunction-active when air class is present in svg
 
    Only add style classes related to user commands with this useEffect. 
    If you need to add styles related to other things such as measurements or device alarms,
    you need to create a separate hook or function for that (check "updateDeviceAlarmEffects()" -funtion for example)
  
    NOTE! Alarm styles are handled with updateDeviceAlarmEffects().
  */
  useEffect(() => {
    if (!svgRoot || !serviceModeStatus) {
      return;
    }

    // From deviceStateToGraph, get all the commands that are active (activeState = true)
    const listOfActiveClasses = [];
    deviceStateToGraph.forEach(device => {
      device.commandStates.forEach(commandState => {
        if (commandState.activeState) {
          const commandNameWithoutWhiteSpacesOrSlashes = commandState.commandName.replace(/[\s\/]+/g, '');
          listOfActiveClasses.push(commandNameWithoutWhiteSpacesOrSlashes.toLowerCase());
        }
        // Use pending animations
        const buttonElement = svgRoot?.querySelector(commandState.buttonTag);
        if (commandState.isPending === true) {
          const blinkingStyle = commandState.activeState ? 'green-blink' : 'orange-blink';
          buttonElement?.querySelector('path')?.classList.add('pending-single');
          if (commandState.blink === true) {
            buttonElement?.querySelector('rect')?.classList.add(blinkingStyle);
          }
        } else {
          buttonElement?.querySelector('path')?.classList.remove('pending-single');
          buttonElement?.querySelector('rect')?.classList.remove('orange-blink');
          buttonElement?.querySelector('rect')?.classList.remove('green-blink');
        }


      });
    });

    // When edge unit in on automatic control, add style classes: "default" and "disabled"
    if (serviceModeStatus === "AutoActive") {
      listOfActiveClasses.push("default");
      listOfActiveClasses.push("disabled");
    }

    // If listOfActiveClasses doesn't contain any value from gasClasses, add default class to the list.
    if (listOfActiveClasses.length === 0) {
      listOfActiveClasses.push("default");
    }
    // Remove all old classes
    svgRoot.classList.remove(...svgRoot.classList);
    // Add all new classes
    svgRoot.classList.add(...listOfActiveClasses);
    return;

  }, [svgRoot, deviceStateToGraph, serviceModeStatus]);

  // Change button colors based on deviceStateToGraph
  useEffect(() => {
    if (!svgRoot) {
      return;
    }
    deviceStateToGraph.forEach(device => {
      device.commandStates.forEach(commandState => {

        const button = svgRoot?.querySelector(commandState.buttonTag);
        const indicator = commandState.indicator ? commandState.indicator : 'rect';
        const activeColor = commandState.colors ? commandState.colors[0] : status.warning;
        const inactiveColor = commandState.colors ? commandState.colors[1] : graphicalDevice.stateInactive;
        const buttonBg = button?.querySelector(indicator);
        if (buttonBg) {
          if (commandState.activeState !== undefined) {
            if (serviceModeStatus === "ManualActive" || commandState.independent) {
              buttonBg.style.fill = commandState.activeState ? activeColor : inactiveColor;
            } else {
              buttonBg.style.fill = graphicalDevice.buttonInactiveBg;
            }
          } else {
            if (!commandState.independent) {
              buttonBg.style.fill = serviceModeStatus !== "ManualActive" ? graphicalDevice.buttonInactiveBg : null;
            }
          }
        }
      });
    });
  }, [deviceStateToGraph, svgRoot, serviceModeStatus]);

  // GraphicalViewCems return statement
  return <div className="svg-wrapper" ref={svgContainerRef} />;
}

export default GraphicalView2D;